import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AgentSelection from '../components/AgentSelection';
import { AgentType } from '../types/auth';
import { Box, CircularProgress, Typography } from '@mui/material';
import { isAdminDomain, redirectToDomain } from '../utils/domainUtils';

const PostLogin: React.FC = () => {
  const { currentUser, userProfile, userPermissions, selectedAgent, selectAgent, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If user is not authenticated, redirect to login
    if (!currentUser && !loading) {
      navigate('/login');
      return;
    }

    // Domain-based redirect logic
    const isAdmin = isAdminDomain();
    
    // Super admins should not be on post-login page - redirect to dashboard
    if (userProfile?.role === 'super_admin' && userPermissions && !loading) {
      if (isAdmin) {
        navigate('/');
      } else {
        // Redirect to admin domain
        redirectToDomain('super_admin');
      }
      return;
    }
    
    // Agents with selected agent should go to dashboard
    if (selectedAgent && userPermissions && !loading) {
      navigate('/');
      return;
    }
  }, [currentUser, selectedAgent, userPermissions, loading, navigate, userProfile]);

  const handleAgentSelect = (agentType: AgentType) => {
    selectAgent(agentType);
    // The useEffect will handle the redirect to dashboard
  };

  // Show loading while checking authentication
  if (loading || !userProfile) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // If user is not authenticated, this shouldn't render but just in case
  if (!currentUser) {
    return null;
  }

  return (
    <AgentSelection 
      onAgentSelect={handleAgentSelect}
      userRole={userProfile.role}
    />
  );
};

export default PostLogin;
